import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Euro, Loader2, Plus, Trash2, Save, AlertTriangle, FileText, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const toNum = (v) => (v === "" || v == null ? 0 : parseFloat(v) || 0);

const FIELD_LABELS = {
  data_contratto: "Data contratto",
  tipologia_pianificata_id: "Tipologia pianificata",
  potenza_kw_pianificata: "Potenza pianificata kW",
  tipologia_id: "Tipologia installata",
  potenza_kw: "Potenza installata kW",
  ple: "PLE (Sì/No)",
  ple_importo: "Costo PLE €",
  fermo_deposito: "Fermo Deposito (Sì/No)",
  fermo_deposito_importo: "Costo Fermo Deposito €",
};

const blankContract = () => ({
  id: "", data_contratto: "",
  tipologia_pianificata_id: "", potenza_kw_pianificata: "",
  tipologia_id: "", potenza_kw: "",
  sopralluogo_incluso: true,
  wallbox: false, pratiche_gse: false, pdc: false, commutatore: false, climatizzatore: false,
  ple: null, ple_importo: "", fermo_deposito: null, fermo_deposito_importo: "",
  team: [], note: "", voci_extra_attive: [], spese_extra: [],
});

function validateContract(c) {
  const missing = [];
  if (!c.data_contratto) missing.push("data_contratto");
  if (!c.tipologia_pianificata_id) missing.push("tipologia_pianificata_id");
  if (c.potenza_kw_pianificata === "" || c.potenza_kw_pianificata == null || toNum(c.potenza_kw_pianificata) <= 0) missing.push("potenza_kw_pianificata");
  if (!c.tipologia_id) missing.push("tipologia_id");
  if (c.potenza_kw === "" || c.potenza_kw == null || toNum(c.potenza_kw) <= 0) missing.push("potenza_kw");
  if (c.ple !== true && c.ple !== false) missing.push("ple");
  if (c.ple === true && (c.ple_importo === "" || c.ple_importo == null || toNum(c.ple_importo) <= 0)) missing.push("ple_importo");
  if (c.fermo_deposito !== true && c.fermo_deposito !== false) missing.push("fermo_deposito");
  if (c.fermo_deposito === true && (c.fermo_deposito_importo === "" || c.fermo_deposito_importo == null || toNum(c.fermo_deposito_importo) <= 0)) missing.push("fermo_deposito_importo");
  return missing;
}

export default function EconomicoBlock({ cid }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [contratti, setContratti] = useState(null);
  const [breakdown, setBreakdown] = useState(null); // aggregato + .contratti[]
  const [dirty, setDirty] = useState(false);
  const [missing, setMissing] = useState([]); // [{i, field}]
  const [canSeeAmounts, setCanSeeAmounts] = useState(true);
  const [operai, setOperai] = useState([]);
  const firstRun = useRef(true);
  const debounceRef = useRef(null);

  const toPayload = (list) => ({
    contratti: (list || []).map((c) => ({
      ...c,
      potenza_kw_pianificata: toNum(c.potenza_kw_pianificata),
      potenza_kw: toNum(c.potenza_kw),
      ple: c.ple === true,
      ple_importo: toNum(c.ple_importo),
      fermo_deposito: c.fermo_deposito === true,
      fermo_deposito_importo: toNum(c.fermo_deposito_importo),
      spese_extra: (c.spese_extra || []).map((s) => ({ descrizione: s.descrizione || "", importo: toNum(s.importo) })),
      team: (c.team || []).map((m) => ({ ...m, over_bonus: toNum(m.over_bonus), percentuale: m.percentuale === "" || m.percentuale == null ? 100 : toNum(m.percentuale) })),
    })),
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/clienti/${cid}/economico`);
      setConfig(data.config);
      const list = (data.input?.contratti || []).map((c) => ({ ...blankContract(), ...c }));
      setContratti(list.length ? list : [blankContract()]);
      setBreakdown(data.breakdown);
      setCanSeeAmounts(data.breakdown != null);
      api.get("/users/operai").then((r) => setOperai(r.data || [])).catch(() => {});
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  // Anteprima live
  useEffect(() => {
    if (!contratti) return;
    if (firstRun.current) { firstRun.current = false; return; }
    setDirty(true);
    if (missing.length) {
      const stillMissing = [];
      contratti.forEach((c, i) => validateContract(c).forEach((f) => {
        if (missing.some((m) => m.i === i && m.field === f)) stillMissing.push({ i, field: f });
      }));
      setMissing(stillMissing);
    }
    if (!canSeeAmounts) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post(`/clienti/${cid}/economico/preview`, toPayload(contratti));
        setBreakdown(data.breakdown);
      } catch { /* best-effort */ }
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [contratti]);

  const updateContract = (i, patch) => setContratti((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const removeContract = (i) => {
    if (contratti.length === 1) { toast.error("Deve esserci almeno un contratto"); return; }
    if (!window.confirm("Eliminare questo contratto dalla scheda?")) return;
    setContratti((prev) => prev.filter((_, idx) => idx !== i));
  };
  const addContract = () => setContratti((prev) => [...prev, blankContract()]);

  const save = async () => {
    const allMissing = [];
    contratti.forEach((c, i) => validateContract(c).forEach((f) => allMissing.push({ i, field: f })));
    if (allMissing.length > 0) {
      setMissing(allMissing);
      toast.error("Completa i campi obbligatori evidenziati in tutti i contratti");
      return;
    }
    setMissing([]);
    setSaving(true);
    try {
      const { data } = await api.put(`/clienti/${cid}/economico`, toPayload(contratti));
      const list = (data.input?.contratti || []).map((c) => ({ ...blankContract(), ...c }));
      setContratti(list.length ? list : [blankContract()]);
      setBreakdown(data.breakdown);
      setDirty(false);
      toast.success("Economico cliente salvato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  if (!config || !contratti) return null;

  const tipologie = config.tipologie || [];

  if (tipologie.length === 0) {
    return (
      <div className="space-y-5" data-testid="tab-economico-content">
        <div className="card-shell p-6">
          <div className="text-sm p-4 rounded-md" style={{ background: "var(--surface-muted)", color: "var(--text-muted)" }}>
            Nessuna tipologia configurata per questa commessa. Vai in <b>Commesse → pulsante €</b> per creare il listino economico.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="tab-economico-content">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-bold text-xl flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
          <Euro className="w-5 h-5" style={{ color: "var(--primary)" }} /> Contratti economici
        </h2>
        <button onClick={addContract} className="btn-secondary text-sm" data-testid="add-contract-btn">
          <Plus className="w-4 h-4" /> Aggiungi contratto
        </button>
      </div>

      {contratti.map((c, i) => (
        <ContractCard
          key={i}
          index={i}
          contract={c}
          bd={breakdown?.contratti?.[i]}
          config={config}
          tipologie={tipologie}
          operai={operai}
          canSeeAmounts={canSeeAmounts}
          missing={missing.filter((m) => m.i === i).map((m) => m.field)}
          onChange={(patch) => updateContract(i, patch)}
          onRemove={() => removeContract(i)}
          canRemove={contratti.length > 1}
        />
      ))}

      {/* Totale cliente (somma contratti) */}
      {breakdown && (
        <div className="card-shell p-6" data-testid="eco-cliente-totale-card" style={{ borderColor: "var(--primary)", borderWidth: 2 }}>
          <div className="flex justify-between items-center">
            <span className="font-bold text-base" style={{ fontFamily: "Manrope" }}>
              Totale cliente ({contratti.length} contratt{contratti.length === 1 ? "o" : "i"})
            </span>
            <span className="font-extrabold text-2xl" style={{ color: "var(--primary)" }} data-testid="eco-guadagno-totale">
              {fmt(breakdown.guadagno_totale)}
            </span>
          </div>
          {breakdown.bonus_totale > 0 && (
            <div className="flex justify-between items-center mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
              <span>Totale bonus dipendenti (conteggio separato)</span>
              <span className="font-semibold" data-testid="eco-cliente-bonus-totale">{fmt(breakdown.bonus_totale)}</span>
            </div>
          )}
        </div>
      )}

      {missing.length > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-lg border-2" style={{ borderColor: "#B30000", background: "#FEF2F2" }} data-testid="eco-validation-alert">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#B30000" }} />
          <div className="text-sm" style={{ color: "#B30000" }}>
            <div className="font-bold">Impossibile salvare: completa i campi obbligatori nei contratti evidenziati.</div>
          </div>
        </div>
      )}

      <div className="flex justify-end items-center gap-3">
        {dirty && (
          <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="eco-dirty-badge">
            Modifiche non salvate
          </span>
        )}
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-economico-cliente">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva economico
        </button>
      </div>
    </div>
  );
}

function ContractCard({ index, contract: c, bd, config, tipologie, operai, canSeeAmounts, missing, onChange, onRemove, canRemove }) {
  const inputStyle = { borderColor: "var(--border)", background: "#fff" };
  const err = (field) => missing.includes(field);
  const fieldStyle = (field) => (err(field) ? { borderColor: "#B30000", background: "#FEF2F2", borderWidth: 2 } : inputStyle);
  const lblStyle = (field) => ({ color: err(field) ? "#B30000" : "var(--text-muted)" });
  const setMember = (mi, patch) => onChange({ team: (c.team || []).map((m, idx) => (idx === mi ? { ...m, ...patch } : m)) });
  const [showSpese, setShowSpese] = useState((c.spese_extra || []).length > 0);
  const setSpesa = (si, patch) => onChange({ spese_extra: (c.spese_extra || []).map((s, idx) => (idx === si ? { ...s, ...patch } : s)) });
  const addSpesa = () => { onChange({ spese_extra: [...(c.spese_extra || []), { descrizione: "", importo: "" }] }); setShowSpese(true); };
  const removeSpesa = (si) => onChange({ spese_extra: (c.spese_extra || []).filter((_, idx) => idx !== si) });

  return (
    <div className="card-shell p-6 space-y-4" data-testid={`eco-contract-${index}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold" style={{ background: "var(--primary)", color: "#fff" }}>
            {index + 1}
          </span>
          <h3 className="font-bold text-base" style={{ fontFamily: "Manrope" }}>
            <FileText className="w-4 h-4 inline mr-1" style={{ color: "var(--primary)" }} /> Contratto {index + 1}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1 mr-2" style={lblStyle("data_contratto")}>
              <CalendarClock className="w-3.5 h-3.5" /> Data contratto *
            </label>
            <input type="date" value={c.data_contratto || ""} onChange={(e) => onChange({ data_contratto: e.target.value })}
              className="px-3 py-1.5 text-sm rounded-md border outline-none" style={fieldStyle("data_contratto")} data-testid={`eco-data-contratto-${index}`} />
          </div>
          {canRemove && (
            <button onClick={onRemove} className="p-1.5 rounded hover:bg-red-50" title="Elimina contratto" data-testid={`eco-remove-contract-${index}`}>
              <Trash2 className="w-4 h-4" style={{ color: "#B30000" }} />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider" style={lblStyle("tipologia_pianificata_id")}>Tipologia PIANIFICATA *</label>
          <select value={c.tipologia_pianificata_id || ""} onChange={(e) => onChange({ tipologia_pianificata_id: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={fieldStyle("tipologia_pianificata_id")} data-testid={`eco-tipologia-pianificata-select-${index}`}>
            <option value="">— seleziona tipologia —</option>
            {tipologie.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider" style={lblStyle("potenza_kw_pianificata")}>Potenza pianificata kW *</label>
          <input type="number" step="0.01" placeholder="Es. 6" value={c.potenza_kw_pianificata ?? ""} onChange={(e) => onChange({ potenza_kw_pianificata: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={fieldStyle("potenza_kw_pianificata")} data-testid={`eco-potenza-pianificata-${index}`} />
        </div>
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" checked={!!c.sopralluogo_incluso} onChange={(e) => onChange({ sopralluogo_incluso: e.target.checked })}
            className="w-4 h-4 accent-[var(--primary)]" data-testid={`eco-sopralluogo-check-${index}`} />
          Sopralluogo effettuato
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider" style={lblStyle("tipologia_id")}>Tipologia INSTALLATA *</label>
          <select value={c.tipologia_id || ""} onChange={(e) => onChange({ tipologia_id: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={fieldStyle("tipologia_id")} data-testid={`eco-tipologia-select-${index}`}>
            <option value="">— seleziona tipologia —</option>
            {tipologie.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider" style={lblStyle("potenza_kw")}>Potenza installata kW *</label>
          <input type="number" step="0.01" placeholder="Es. 6" value={c.potenza_kw ?? ""} onChange={(e) => onChange({ potenza_kw: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={fieldStyle("potenza_kw")} data-testid={`eco-potenza-kw-${index}`} />
        </div>
        {c.tipologia_pianificata_id && toNum(c.potenza_kw) > toNum(c.potenza_kw_pianificata) && (
          <div className="pb-2 text-xs font-semibold" style={{ color: "var(--primary)" }} data-testid={`eco-extra-kw-hint-${index}`}>
            +{(toNum(c.potenza_kw) - toNum(c.potenza_kw_pianificata)).toFixed(2)} kW extra attivati
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider" style={lblStyle("ple")}>PLE *</label>
          <select value={c.ple === true ? "si" : c.ple === false ? "no" : ""} onChange={(e) => onChange({ ple: e.target.value === "" ? null : e.target.value === "si" })}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={fieldStyle("ple")} data-testid={`eco-ple-select-${index}`}>
            <option value="">— seleziona —</option>
            <option value="no">No</option>
            <option value="si">Sì</option>
          </select>
        </div>
        {c.ple === true && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={lblStyle("ple_importo")}>Costo PLE € (extra) *</label>
            <input type="number" step="0.01" placeholder="Es. 100" value={c.ple_importo ?? ""} onChange={(e) => onChange({ ple_importo: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={fieldStyle("ple_importo")} data-testid={`eco-ple-importo-${index}`} />
          </div>
        )}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider" style={lblStyle("fermo_deposito")}>Fermo Deposito *</label>
          <select value={c.fermo_deposito === true ? "si" : c.fermo_deposito === false ? "no" : ""} onChange={(e) => onChange({ fermo_deposito: e.target.value === "" ? null : e.target.value === "si" })}
            className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={fieldStyle("fermo_deposito")} data-testid={`eco-fermo-select-${index}`}>
            <option value="">— seleziona —</option>
            <option value="no">No</option>
            <option value="si">Sì</option>
          </select>
        </div>
        {c.fermo_deposito === true && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={lblStyle("fermo_deposito_importo")}>Costo Fermo Deposito € (extra) *</label>
            <input type="number" step="0.01" placeholder="Es. 75" value={c.fermo_deposito_importo ?? ""} onChange={(e) => onChange({ fermo_deposito_importo: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={fieldStyle("fermo_deposito_importo")} data-testid={`eco-fermo-importo-${index}`} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-6 pt-1">
        {[["wallbox", "Wallbox"], ["pratiche_gse", "Pratiche GSE"], ["pdc", "PDC"], ["commutatore", "Commutatore"], ["climatizzatore", "Climatizzatore"]].map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!c[key]} onChange={(e) => onChange({ [key]: e.target.checked })}
              className="w-4 h-4 accent-[var(--primary)]" data-testid={`eco-${key}-check-${index}`} />
            {label}
          </label>
        ))}
      </div>

      {(config?.voci_extra || []).length > 0 && (
        <div className="pt-2 mt-1 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Voci extra</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {(config.voci_extra || []).map((v) => {
              const active = (c.voci_extra_attive || []).includes(v.id);
              return (
                <label key={v.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={active}
                    onChange={(e) => {
                      const cur = c.voci_extra_attive || [];
                      onChange({ voci_extra_attive: e.target.checked ? [...cur, v.id] : cur.filter((x) => x !== v.id) });
                    }}
                    className="w-4 h-4 accent-[var(--primary)]" data-testid={`eco-voce-extra-check-${index}-${v.id}`} />
                  {v.nome}
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>({fmt(v.importo)})</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Spese extra libere (flag che apre la sezione) */}
      <div className="pt-2 mt-1 border-t" style={{ borderColor: "var(--border)" }}>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={showSpese}
            onChange={(e) => { const on = e.target.checked; setShowSpese(on); if (on && (c.spese_extra || []).length === 0) addSpesa(); }}
            className="w-4 h-4 accent-[var(--primary)]" data-testid={`eco-spese-extra-flag-${index}`} />
          Spese extra
          {(c.spese_extra || []).length > 0 && <span className="text-xs" style={{ color: "var(--text-muted)" }}>({(c.spese_extra || []).length})</span>}
        </label>
        {showSpese && (
          <div className="mt-2 space-y-2">
            {(c.spese_extra || []).map((s, si) => (
              <div key={si} className="flex items-center gap-2" data-testid={`eco-spesa-row-${index}-${si}`}>
                <input value={s.descrizione || ""} onChange={(e) => setSpesa(si, { descrizione: e.target.value })}
                  placeholder="Descrizione spesa (es. Noleggio gru)" className="flex-1 min-w-[180px] px-2 py-1.5 text-sm rounded-md border outline-none"
                  style={inputStyle} data-testid={`eco-spesa-desc-${index}-${si}`} />
                {canSeeAmounts && (
                  <input type="number" step="0.01" value={s.importo ?? ""} onChange={(e) => setSpesa(si, { importo: e.target.value })}
                    placeholder="€" className="w-28 px-2 py-1.5 text-sm rounded-md border outline-none text-right"
                    style={inputStyle} data-testid={`eco-spesa-importo-${index}-${si}`} />
                )}
                <button onClick={() => removeSpesa(si)} className="p-1 rounded hover:bg-black/5" data-testid={`eco-spesa-remove-${index}-${si}`}>
                  <Trash2 className="w-3.5 h-3.5" style={{ color: "#B30000" }} />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button onClick={addSpesa} className="btn-secondary text-xs" data-testid={`eco-spesa-add-${index}`}>
                <Plus className="w-3.5 h-3.5" /> Aggiungi spesa extra
              </button>
              {canSeeAmounts && bd && bd.spese_extra_totale > 0 && (
                <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
                  Totale spese extra (sommate): <span style={{ color: "var(--primary)" }} data-testid={`eco-spese-extra-totale-${index}`}>+ {fmt(bd.spese_extra_totale)}</span>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Spaccato del contratto */}
      {bd && (
        <div className="rounded-md p-4" style={{ background: "var(--surface-muted)" }} data-testid={`eco-breakdown-${index}`}>
          <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Spaccato fatturato contratto</div>
          <div className="space-y-1.5 text-sm">
            <Row label="Sopralluogo" value={fmt(bd.sopralluogo)} />
            <Row label={`Installazione${bd.tipologia_pianificata ? ` — ${bd.tipologia_pianificata.nome}` : bd.tipologia ? ` — ${bd.tipologia.nome}` : ""}`} value={fmt(bd.installazione)} />
            {bd.extra_kw > 0 && <Row label={`Extra kW (+${bd.extra_kw} kW × ${fmt(bd.extra_kw_unitario)})`} value={fmt(bd.extra_tipologia)} />}
            {bd.ple && <Row label="PLE (extra)" value={fmt(bd.ple_importo)} />}
            {bd.fermo_deposito && <Row label="Fermo Deposito (extra)" value={fmt(bd.fermo_deposito_importo)} />}
            {bd.wallbox && <Row label="Wallbox" value={fmt(bd.wallbox_importo)} />}
            {bd.pratiche_gse && <Row label="Pratiche GSE" value={fmt(bd.pratiche_gse_importo)} />}
            {bd.pdc && <Row label="PDC" value={fmt(bd.pdc_importo)} />}
            {bd.commutatore && <Row label="Commutatore" value={fmt(bd.commutatore_importo)} />}
            {bd.climatizzatore && <Row label="Climatizzatore" value={fmt(bd.climatizzatore_importo)} />}
            {(bd.voci_extra || []).filter((v) => v.attiva).map((v) => <Row key={v.id} label={v.nome} value={fmt(v.importo)} />)}
            {(bd.spese_extra || []).map((s, si) => <Row key={`sp-${si}`} label={s.descrizione || "Spesa extra"} value={`+ ${fmt(s.importo)}`} />)}
            {bd.extra_prodotti_totale > 0 && <Row label="Extra prodotti" value={fmt(bd.extra_prodotti_totale)} />}
            <div className="flex justify-between items-center pt-2 mt-1 border-t" style={{ borderColor: "var(--border)" }}>
              <span className="font-bold">Totale contratto</span>
              <span className="font-extrabold" style={{ color: "var(--primary)" }} data-testid={`eco-contract-totale-${index}`}>{fmt(bd.guadagno_totale)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Bonus per contratto */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-bold text-sm" style={{ fontFamily: "Manrope" }}>Bonus dipendenti (contratto)</h4>
          <button onClick={() => onChange({ team: [...(c.team || []), { nome: "", ruolo: "installatore", affiancamento: false, over_bonus: 0, percentuale: 100 }] })}
            className="btn-secondary text-xs" data-testid={`add-team-member-${index}`}>
            <Plus className="w-3.5 h-3.5" /> Aggiungi persona
          </button>
        </div>
        {(c.team || []).map((m, mi) => (
          <div key={mi} className="flex flex-wrap items-center gap-2 mb-2" data-testid={`team-row-${index}-${mi}`}>
            <select value={m.nome} onChange={(e) => setMember(mi, { nome: e.target.value })}
              className="flex-1 min-w-[150px] px-2 py-1.5 text-sm rounded-md border outline-none" style={inputStyle} data-testid={`team-nome-${index}-${mi}`}>
              <option value="">— Seleziona operaio —</option>
              {m.nome && !operai.some((o) => o.name === m.nome) && <option value={m.nome}>{m.nome} (non in anagrafica)</option>}
              {operai.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
            </select>
            <select value={m.ruolo} onChange={(e) => setMember(mi, { ruolo: e.target.value, affiancamento: e.target.value === "responsabile" ? false : m.affiancamento })}
              className="px-2 py-1.5 text-sm rounded-md border outline-none" style={inputStyle} data-testid={`team-ruolo-${index}-${mi}`}>
              <option value="responsabile">Responsabile squadra</option>
              <option value="supervisore">Supervisore squadre</option>
              <option value="installatore">Installatore</option>
            </select>
            {m.ruolo === "installatore" && (
              <label className="flex items-center gap-1.5 text-xs">
                <input type="checkbox" checked={!!m.affiancamento} onChange={(e) => setMember(mi, { affiancamento: e.target.checked })}
                  className="w-4 h-4 accent-[var(--primary)]" data-testid={`team-affiancamento-${index}-${mi}`} />
                Affiancamento
              </label>
            )}
            {canSeeAmounts && (
              <>
                <label className="text-xs" style={{ color: "var(--text-muted)" }} title="Quota % del bonus base">Quota %</label>
                <input type="number" min="0" max="100" step="5" value={m.percentuale ?? 100} onChange={(e) => setMember(mi, { percentuale: e.target.value })}
                  className="w-20 px-2 py-1.5 text-sm rounded-md border outline-none" style={inputStyle} data-testid={`team-percentuale-${index}-${mi}`} />
                <label className="text-xs" style={{ color: "var(--text-muted)" }}>Over bonus €</label>
                <input type="number" step="0.01" value={m.over_bonus} onChange={(e) => setMember(mi, { over_bonus: e.target.value })}
                  className="w-24 px-2 py-1.5 text-sm rounded-md border outline-none" style={inputStyle} data-testid={`team-overbonus-${index}-${mi}`} />
              </>
            )}
            <button onClick={() => onChange({ team: c.team.filter((_, idx) => idx !== mi) })}
              className="p-1 rounded hover:bg-black/5" data-testid={`remove-team-${index}-${mi}`}>
              <Trash2 className="w-3.5 h-3.5" style={{ color: "#B30000" }} />
            </button>
          </div>
        ))}
        {bd && bd.bonus && bd.bonus.length > 0 && (
          <div className="mt-2 p-3 rounded-md" style={{ background: "var(--surface-muted)" }}>
            {bd.bonus.map((b, bi) => (
              <div key={bi} className="flex justify-between text-sm py-0.5" data-testid={`bonus-calc-row-${index}-${bi}`}>
                <span>{b.nome || "—"} <span className="text-xs" style={{ color: "var(--text-muted)" }}>({b.ruolo}{b.affiancamento ? " · affiancamento" : ""}{b.nota ? ` · ${b.nota}` : ""})</span></span>
                <span className="font-semibold">{fmt(b.totale)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 mt-1 border-t font-bold" style={{ borderColor: "var(--border)" }}>
              <span>Totale bonus contratto</span>
              <span data-testid={`eco-bonus-totale-${index}`}>{fmt(bd.bonus_totale)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
